import React, { useState, useEffect } from 'react';
import { Box, IconButton, Tooltip, Fade } from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

// Tags an agent's text extractor cares about, in document order via TreeWalker
const CONTENT_SELECTOR = 'h1, h2, h3, h4, h5, h6, p, a[href], img, button, input, textarea, select, li, blockquote, [role="button"]';

const isVisible = (el) => {
  if (el.closest('#agent-view-overlay')) return false;
  const style = window.getComputedStyle(el);
  if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
  const rect = el.getBoundingClientRect();
  return rect.width > 0 || rect.height > 0;
};

const ownText = (el) => (el.innerText || '').trim().replace(/\s+/g, ' ');

// Walk the live DOM and produce the markdown-ish text an AI agent would extract
export const extractAgentView = () => {
  const lines = [];
  const seen = new Set();
  const push = (line) => {
    if (line && !seen.has(line)) {
      seen.add(line);
      lines.push(line);
    }
  };

  // Metadata agents read first
  push(`URL: ${window.location.href}`);
  push(`Title: ${document.title}`);
  const desc = document.querySelector('meta[name="description"]');
  if (desc?.content) push(`Description: ${desc.content}`);
  push('');
  push('--- PAGE CONTENT ---');
  push('');

  document.querySelectorAll(CONTENT_SELECTOR).forEach((el) => {
    if (!isVisible(el)) return;
    const tag = el.tagName.toLowerCase();

    if (/^h[1-6]$/.test(tag)) {
      const text = ownText(el);
      if (text) push(`${'#'.repeat(Number(tag[1]))} ${text}`);
    } else if (tag === 'img') {
      const alt = (el.getAttribute('alt') || '').trim();
      push(alt ? `[image: ${alt}]` : '[image: ⚠ NO ALT TEXT — this image is invisible to agents]');
    } else if (tag === 'a') {
      const text = ownText(el);
      const href = el.getAttribute('href');
      if (text) push(`[${text}](${href})`);
      else push(`[⚠ link with no text](${href})`);
    } else if (tag === 'button' || el.getAttribute('role') === 'button') {
      const label = ownText(el) || el.getAttribute('aria-label') || '';
      push(label ? `(button: ${label})` : '(button: ⚠ unlabeled — agents cannot tell what this does)');
    } else if (tag === 'input' || tag === 'textarea' || tag === 'select') {
      const label =
        el.getAttribute('aria-label') ||
        el.getAttribute('placeholder') ||
        el.getAttribute('name') ||
        '⚠ unlabeled field';
      push(`(input: ${label})`);
    } else if (tag === 'li') {
      // Only leaf list items — avoid duplicating nested content
      if (!el.querySelector('li') && el.children.length === 0) {
        const text = ownText(el);
        if (text) push(`- ${text}`);
      }
    } else {
      // p, blockquote — skip if a child element already produces the same line
      if (el.querySelector('a, button, img')) {
        // Mixed content: keep the text, links are emitted separately
        const text = ownText(el);
        if (text && text.length > 2) push(text);
      } else {
        const text = ownText(el);
        if (text && text.length > 2) push(text);
      }
    }
  });

  // Structured data (JSON-LD) — the highest-signal thing for agents
  const jsonLd = document.querySelectorAll('script[type="application/ld+json"]');
  if (jsonLd.length) {
    push('');
    push('--- STRUCTURED DATA (JSON-LD) ---');
    jsonLd.forEach((s) => {
      try {
        push(JSON.stringify(JSON.parse(s.textContent), null, 2));
      } catch {
        /* ignore malformed blocks */
      }
    });
  }

  return lines.join('\n');
};

// Single navbar pill that switches to the *other* view:
// human view shows "Agent View", agent view shows "Human View".
// Label hides below the lg breakpoint (icon-only), matching the other nav buttons.
export const AgentToggle = ({ open, onToggle, size = 'medium' }) => {
  const compact = size === 'small';
  const label = open ? 'Human View' : 'Agent View';

  return (
    <Tooltip title={open ? 'Back to the normal website' : 'See this page the way AI agents read it'} arrow>
      <Box
        component="button"
        onClick={onToggle}
        aria-pressed={open}
        aria-label={label}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.8,
          px: compact ? 1 : 1.5,
          py: compact ? 0.8 : 1,
          borderRadius: '10px',
          border: '1px solid rgba(0, 121, 107, 0.25)',
          cursor: 'pointer',
          fontWeight: 600,
          fontSize: '0.9rem',
          fontFamily: 'inherit',
          whiteSpace: 'nowrap',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          color: open ? '#00e5c0' : '#00796b',
          background: open ? '#0d1117' : 'rgba(0, 121, 107, 0.06)',
          '&:hover': {
            background: open ? '#161b22' : 'rgba(0, 121, 107, 0.12)',
            transform: 'translateY(-1px)',
          },
        }}
      >
        {open ? <PersonIcon sx={{ fontSize: 20 }} /> : <SmartToyIcon sx={{ fontSize: 20 }} />}
        {!compact && (
          <Box component="span" sx={{ display: { xs: 'none', lg: 'inline' } }}>
            {label}
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};

const AgentView = ({ open, onToggle }) => {
  const [content, setContent] = useState('');
  const [copied, setCopied] = useState(false);
  const [navHeight, setNavHeight] = useState(84);

  useEffect(() => {
    if (open) {
      setContent(extractAgentView());
      // Align the overlay flush under the navbar (its height shrinks on scroll)
      const appBar = document.querySelector('.MuiAppBar-root');
      if (appBar) setNavHeight(appBar.getBoundingClientRect().height);
      // Lock the page behind the overlay so the human view can't scroll underneath
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prevOverflow;
      };
    }
  }, [open]);

  const handleCopy = () => {
    navigator.clipboard.writeText(content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <>
      {/* Terminal-style overlay */}
      <Fade in={open}>
        <Box
          id="agent-view-overlay"
          role="dialog"
          aria-label="Agent view of this page"
          sx={{
            position: 'fixed',
            top: `${navHeight}px`,
            left: 0,
            right: 0,
            bottom: 0,
            // Below the AppBar so the navbar (and its Human/Agent toggle) stays usable
            zIndex: (theme) => theme.zIndex.drawer,
            display: open ? 'flex' : 'none',
            flexDirection: 'column',
            bgcolor: '#0d1117',
            color: '#c9d1d9',
          }}
        >
          {/* Header bar */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 3,
              py: 1.5,
              borderBottom: '1px solid #21262d',
              flexShrink: 0,
            }}
          >
            <SmartToyIcon sx={{ color: '#00e5c0' }} />
            <Box sx={{ fontFamily: 'monospace', fontSize: 14, color: '#00e5c0', fontWeight: 700 }}>
              agent://bixbite.ai — how AI assistants read this page
            </Box>
            <Box sx={{ flex: 1 }} />
            <Tooltip title={copied ? 'Copied!' : 'Copy as markdown'} arrow>
              <IconButton onClick={handleCopy} size="small" sx={{ color: '#8b949e' }} aria-label="Copy agent view">
                {copied ? <CheckIcon fontSize="small" sx={{ color: '#00e5c0' }} /> : <ContentCopyIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <IconButton onClick={onToggle} size="small" sx={{ color: '#8b949e' }} aria-label="Close agent view">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Explainer strip */}
          <Box
            sx={{
              px: 3,
              py: 1,
              fontSize: 13,
              color: '#8b949e',
              borderBottom: '1px solid #21262d',
              flexShrink: 0,
            }}
          >
            No colors, no animations, no layout — AI agents extract text, headings, links, alt text and
            labels. Lines marked ⚠ are invisible or confusing to agents.
          </Box>

          {/* Extracted content */}
          <Box
            component="pre"
            sx={{
              flex: 1,
              overflow: 'auto',
              m: 0,
              px: 3,
              py: 2,
              fontFamily: '"SF Mono", "Fira Code", Menlo, Consolas, monospace',
              fontSize: 13.5,
              lineHeight: 1.7,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {content}
          </Box>
        </Box>
      </Fade>
    </>
  );
};

export default AgentView;
